import { AggregateScoreType, useGetAggregateScoresQuery } from "~/operations";
import { AggregateScoreOrderField, OrderDirection } from "~/operations";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { useState } from "react";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import { Header } from "~/types/table";

type SortField = AggregateScoreOrderField | "EFFORT" | "RISK_IMPROVEMENT";

export const useTopActionsCard = (scope: SpaceOrWorkspaceScope) => {
  const { isFeatureEnabled } = useFeatureFlags();
  const isMoveTheNeedleFeatureEnabled = isFeatureEnabled("Move the needle");

  // we're managing the sorting state client-side because we only ever want the top 30 riskiest findings
  const [sortField, setSortField] = useState<SortField>("RISK_IMPROVEMENT");
  const [sortDirection, setSortDirection] = useState<OrderDirection>(
    OrderDirection.Desc,
  );

  const [page, setPage] = useState(1);

  const { data, loading, refetch } = useGetAggregateScoresQuery({
    variables: {
      first: 30,
      entityMrn: scope.mrn,
      after: null,
      filter: {
        maxRiskScore: 99,
        hasRemediation: true,
      },
      orderBy: {
        field: AggregateScoreOrderField.Rank,
        direction: OrderDirection.Asc,
      },
    },
    skip: !scope || !isMoveTheNeedleFeatureEnabled,
  });

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setPage(value);
  };

  const handleSortChange = (field: SortField, direction: OrderDirection) => {
    setSortField(field);
    setSortDirection(direction);
    setPage(1);
  };

  const aggregateScores =
    data?.aggregateScores?.__typename === "AggregateScoresConnection"
      ? data.aggregateScores
      : undefined;

  const findingsEdges = aggregateScores?.edges || [];
  const totalFindings = findingsEdges.length || 0;
  const findingsNodes = findingsEdges.flatMap((edge) => edge.node ?? []);
  const formattedFindings = findingsNodes.map((node) => {
    return {
      ...node,
      title:
        node.scoreType === AggregateScoreType.Advisory
          ? node.findingMrn.split("/").pop() || ""
          : node.title,
    };
  });
  const findings = formattedFindings
    .sort((a, b) => {
      const direction = sortDirection === OrderDirection.Asc ? 1 : -1;
      const sortByRank = () => a.rank - b.rank;
      const sortByRisk = () => a.spaceRiskImprovement - b.spaceRiskImprovement;
      const sortByTitle = () => a.title.localeCompare(b.title);
      const sortByAffected = () =>
        (a.blastRadius?.affected ?? 0) - (b.blastRadius?.affected ?? 0);
      const sortByEffort = () => a.effort - b.effort;
      const position =
        (() => {
          switch (sortField) {
            case AggregateScoreOrderField.Title:
              return sortByTitle();
            case AggregateScoreOrderField.BlastRadius:
              return sortByAffected();
            case "EFFORT":
              return sortByEffort();
            case "RISK_IMPROVEMENT":
            default:
              return sortByRisk();
          }
        })() || sortByRank();
      return position * direction;
    })
    .slice((page - 1) * 5, page * 5);

  // get the total number of pages
  const totalPages = Math.ceil(findingsNodes.length / 5);

  const tableHeaders: Header<SortField>[] = [
    {
      id: "TOP_ACTIONS_FINDING",
      sortField: AggregateScoreOrderField.Title,
      label: "Finding",
      options: {
        textAlign: undefined,
        sx: {
          flex: 1,
        },
      },
    },
    {
      id: "ASSETS_COUNT",
      sortField: AggregateScoreOrderField.BlastRadius,
      label: "Assets",
      options: undefined,
    },

    {
      id: "EFFORT",
      sortField: "EFFORT",
      label: "Mondoo Effort",
      options: undefined,
    },
    {
      id: "ACTION",
      sortField: undefined,
      label: "",
      options: {
        textAlign: "right",
        width: 159,
      },
    },
  ] as const;

  return {
    findings,
    totalFindings,
    loading,
    refetch,
    tableHeaders,
    sortField,
    sortDirection,
    handleSortChange,
    page,
    handlePageChange,
    totalPages,
  };
};
